import { SearchBarSkipKeys } from "projects/journal/src/_interface/searchbar.enum";
import { DiagnosisEnum, DiagnosisStatusEnum, DiagnosisTranslations } from "../../widgets/diagnosis/_interface/diagnosis-value.enum";
import { MedicineEnum, MedicineTranslations } from "../../widgets/medicine/_interface/medicine-value.enum";
import { PulseEnum, PulseTranslations } from "../../widgets/pulse/_interface/pulse-value.enum";

function cleanText(str: string): string {
	return str
		.replace(/<[^>]*>/g, ' ')
		.replace(/\s+/g, ' ')
		.replace(/\s+([.,!?;:])/g, '$1')
		.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?Z?/g, '')
		.trim()
		.toLowerCase();
}

function extractAllText(obj: any): string {
	const SKIP_KEYS = new Set(Object.values(SearchBarSkipKeys));
	if (obj == null) return '';
	if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
		return cleanText(String(obj)) + ' ';
	}
	if (Array.isArray(obj)) {
		return obj.map(item => extractAllText(item)).join(' ');
	}
	if (typeof obj === 'object') {
		return Object.entries(obj)
			.filter(([key]) => !SKIP_KEYS.has(key as SearchBarSkipKeys))
			.map(([, val]) => extractAllText(val))
			.join(' ');
	}
	return '';
}

function extractAllDates(obj: any, result: string[] = []): string[] {
	if (obj == null) return result;
	if (obj instanceof Date) {
		result.push(formatDateToDDMMYYYY(obj));
		result.push(formatDateToDDMMMYYYY(obj));
	} else if (typeof obj === 'string') {
		if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obj)) {
			const tempDate = parseDateSafe(obj);
			result.push(formatDateToDDMMYYYY(tempDate));
			result.push(formatDateToDDMMMYYYY(tempDate));
		}
	} else if (Array.isArray(obj)) {
		for (const item of obj) extractAllDates(item, result);
	} else if (typeof obj === 'object') {
		for (const val of Object.values(obj)) extractAllDates(val, result);
	}
	return result;
}

function formatDateToDDMMYYYY(dateInput: Date | string): string {
	const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
	const useUTC =
		typeof dateInput === 'string' &&
		(dateInput.endsWith('Z') || dateInput.includes('+00:00'));
	const day = String(useUTC ? date.getUTCDate() : date.getDate()).padStart(2, '0');
	const month = String((useUTC ? date.getUTCMonth() : date.getMonth()) + 1).padStart(2, '0');
	const year = useUTC ? date.getUTCFullYear() : date.getFullYear();
	return `${day}.${month}.${year}`;
}

export function formatDateToDDMMMYYYY(date: Date): string {
	return date.toLocaleDateString('da-DK', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
}

function parseDateSafe(obj: string): Date {
	if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obj)) {
		return new Date(NaN);
	}
	const parsed = new Date(obj);
	return parsed;
}

export function isValidDanishDateInput(input: string): boolean {
	const trimmed = input.trim();
	if (/\.\./.test(trimmed)) return false;
	const datePatterns = [
		/^\d{0,2}(\.\d{0,2}(\.\d{0,4})?)?$/,
		/^\d{0,2}\s?[A-Za-zæøåÆØÅ]{0,4}\.?(\s?\d{0,4})?$/,
		/^\d{0,2}\.?\s?[A-Za-zæøåÆØÅ]{0,4}\.?(\s?\d{0,4})?$/,
		/^\d{0,2}\s?[A-Za-zæøåÆØÅ]{0,4}\.?(\s?\d{0,4})?(\s?-\s?\d{0,2}:?\d{0,2})?$/
	];
	return datePatterns.some(p => p.test(trimmed));
}

export function findPulseEnum(value: string): PulseEnum | null {
	const normalized = value.trim().toLowerCase();
	for (const [key, translations] of Object.entries(PulseTranslations)) {
		if (
			translations.en.some(t => t.toLowerCase().includes(normalized)) ||
			translations.da.some(t => t.toLowerCase().includes(normalized))
		) {
			return key as PulseEnum;
		}
	}
	return null; 
}

export function findMedicineEnum(value: string): MedicineEnum | null {
	const normalized = value.trim().toLowerCase();
	for (const [key, translations] of Object.entries(MedicineTranslations)) {
		if (
			translations.en.some(t => t.toLowerCase().includes(normalized)) ||
			translations.da.some(t => t.toLowerCase().includes(normalized))
		) {
			return key as MedicineEnum;
		}
	}
	return null;
}

export function matchesDiagnosisStatus(keyword: string, active: boolean): boolean {
	const normalized = keyword.trim().toLowerCase();
	if (DiagnosisStatusEnum.DanishActive.includes(normalized) || DiagnosisStatusEnum.EnglishActive.includes(normalized)) {
		if(active){
			return true;
		}	
	}
	if (DiagnosisStatusEnum.DanishInactive.includes(normalized) || DiagnosisStatusEnum.EnglishInactive.includes(normalized)) {
		if (!active) {
			return true;
		}	
	}
	if (DiagnosisStatusEnum.StatusLabel.includes(keyword)) {
		return true;
	}
	return false;
}

export function findDiagnosisEnum(value: string): DiagnosisEnum | null {
	const normalized = value.trim().toLowerCase();
	for (const [key, translations] of Object.entries(DiagnosisTranslations)) {
		if (
			translations.en.some(t => t.toLowerCase().includes(normalized)) ||
			translations.da.some(t => t.toLowerCase().includes(normalized))
		) {
			return key as DiagnosisEnum;
		}
	}
	return null;
}

function getFieldValue(field: any): string | string[] | null {
	if (!field) return null;
	const lastEntry = field?.content?.lastEntry ?? null;
	const history = field?.content?.history ?? [];
	function extractFromEntry(entry: any, fieldType: string): string | string[] | null {
		if (!entry) return null;
		switch (fieldType) {
			case "text":
			case "longtext":
			case "richtext":
				return entry?.textContent ?? null;
			case "select":
			case "radio":
				return entry?.selected ?? entry?.selectedDescription ?? null;
			case "checkbox":
				return entry?.selected ?? [];
			case "multiselect":
				return entry?.selected?.map((s: any) => s?.key ?? "") ?? [];
			case "date":
				return entry?.date ?? null;
			case "file":
				return entry?.fileName ?? null;
			default:
				return null;
		}
	}

	let value = extractFromEntry(lastEntry, field.fieldType);
	if (value != null && (Array.isArray(value) ? value.length > 0 : String(value).trim() !== "")) {
		return value;
	}
	for (let i = history.length - 1; i >= 0; i--) {
		value = extractFromEntry(history[i], field.fieldType);
		if (value != null && (Array.isArray(value) ? value.length > 0 : String(value).trim() !== "")) {
			return value;
		}
	}
	return null;
}

export function hasFieldValue(field: any): boolean {
	const value = getFieldValue(field);

	if (value == null) return false;
	if (Array.isArray(value)) return value.length > 0;
	if (typeof value === "string") return value.trim().length > 0;

	return true;
}

function canonicalize(s: string): string {
	return s
		.toLowerCase()
		.replace(/\u00a0/g, ' ')
		.replace(/([a-zæøå])\./g, '$1')
		.replace(/\./g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function checkForDateInput(obj: any, trimmedKeyword: string) {
	const looksLikeDate = /\d/.test(trimmedKeyword) || /[A-Za-zæøåÆØÅ]/.test(trimmedKeyword);
	if (looksLikeDate && isValidDanishDateInput(trimmedKeyword)) {
		const canonicalKeyword = canonicalize(trimmedKeyword);	
		const cleanedObj = (function removeModificationDate(o: any): any {
			if (Array.isArray(o)) {
				return o.map(removeModificationDate);
			} else if (o && typeof o === 'object') {
				const copy: any = {};
				for (const key of Object.keys(o)) {
					if (key === 'modificationDate') continue;
					copy[key] = removeModificationDate(o[key]);
				}
				return copy;
			}
			return o;
		})(obj);
		const extractedDates = extractAllDates(cleanedObj)
			.map(d => d.toLowerCase())
			.filter(Boolean);
		const canonicalDates = extractedDates.map(canonicalize);
		if (canonicalDates.some(d => d.includes(canonicalKeyword))) {
			return true;
		}
	}
	return false;
}
export function searchInObject(obj: any, keyword: string): boolean {
	const trimmedKeyword = keyword.trim();
	if (!trimmedKeyword) return false;
	if(checkForDateInput(obj, trimmedKeyword)) return true;
	const fullText = extractAllText(obj);
	const normalizedKeyword = cleanText(trimmedKeyword);
	return fullText.includes(normalizedKeyword);
}
